import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { isNotNil } from "src/utils/type_guards";
import { DomainsCircle } from "../../graphics/domains_circle";

import { meta } from "./meta";

/*
 * Types
 */

export type IntellectualProps = {
    readonly reports: Array<any>;
};

/*
 * Main
 */

export const Intellectual = (props: IntellectualProps): React.ReactElement => {
    const { reports } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="intellectual" inline />
                <PageTitle style={{ color: "#99b84e" }}>Intellectual</PageTitle>
                <Body>
                    The Christian faith is one that can be known (Luke 1:1-4; 1 John 5:13). Faith is formed at least in
                    part in the mind and the Bible often describes faith as belief (Mark 1:15; John 20:31; 2
                    Thessalonians 2:13). Thus, Christian schools seek to train students’ minds and to prepare them to
                    view the world from a biblical perspective.
                </Body>
                <Body>
                    A Christian school engages in many intellectual practices to train minds and develop a biblical
                    worldview. FFI data analysis identified constructs related to the Intellectual domain for six of the
                    seven constituent groups (students; alumni; teachers; leaders/administrators; support staff; and
                    board members), below.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FFI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(reports[0].node.results.raw.intellectual),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle style={{ color: "#99b84e" }}>{m.title}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        backgroundColor="#99b94e"
                                        data={reports
                                            .map((r) => ({
                                                label: r.node.year,
                                                value: r.node.results.raw.intellectual[construct] * 100,
                                            }))
                                            .concat({
                                                label: "FFI Benchmark",
                                                value: reports[0].node.results.benchmarks.intellectual[construct] * 100,
                                            })}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
